/**
 * @fileoverview Controller for the public /pricing marketing page.
 */

import { SUBSCRIPTION_TIERS, formatTierPricing } from './config/subscriptionTiers.js';

let isYearlyBilling = true;
let isFreeMode = true;
let subscriptionPricing = {
    advanced: { monthly: 12, yearly: 100 },
    pro: { monthly: null, yearly: 199 },
    unlimited: { monthly: null, yearly: 1999 }
};

/**
 * Initializes pricing page configuration, feature flag checks, and billing toggles.
 */
export async function initPricing() {
    try {
        const [flagsRes, pricingRes] = await Promise.all([
            fetch('/api/feature-flags'),
            fetch('/api/free-mode-pricing')
        ]);

        if (flagsRes.ok) {
            const flags = await flagsRes.json();
            isFreeMode = flags.free_mode !== false;
        }

        if (pricingRes.ok) {
            subscriptionPricing = await pricingRes.json();
        }
    } catch (e) {
        console.warn('Could not load pricing configuration, using defaults:', e);
    }

    const toggleContainer = document.getElementById('billing-toggle-container');
    const toggleMonthly = document.getElementById('opt-monthly');
    const toggleYearly = document.getElementById('opt-yearly');
    const billingToggle = document.getElementById('billing-toggle');

    if (isFreeMode) {
        if (toggleContainer) toggleContainer.style.display = 'flex';
        billingToggle?.addEventListener('click', () => {
            isYearlyBilling = !isYearlyBilling;
            toggleMonthly?.classList.toggle('active', !isYearlyBilling);
            toggleYearly?.classList.toggle('active', isYearlyBilling);
            renderFreeModeTiers();
        });
        renderFreeModeTiers();
    } else {
        if (toggleContainer) toggleContainer.style.display = 'none';
        renderCreditPacks();
    }
}

/**
 * Renders Free Mode subscription tiers using the centralized subscription tiers definition.
 */
export function renderFreeModeTiers() {
    const grid = document.getElementById('pricing-grid');
    if (!grid) return;

    grid.innerHTML = SUBSCRIPTION_TIERS.map(tier => {
        const pricingInfo = subscriptionPricing[tier.key];
        const display = (tier.price === '$0') ? { price: '$0', period: 'Free forever' } : formatTierPricing(pricingInfo, isYearlyBilling);

        const badgeHtml = tier.badge ? `<div class="tier-badge">${tier.badge}</div>` : '';
        const featuresHtml = tier.features.map(f => `
            <li class="${f.enabled ? '' : 'disabled'}">
                <span class="${f.enabled ? 'check' : 'disabled'}">${f.enabled ? '✓' : '✕'}</span>
                <span>${f.text}</span>
            </li>
        `).join('');

        const btnClass = tier.buttonSecondary ? 'btn btn-secondary tier-btn' : 'btn btn-primary tier-btn';

        return `
            <div class="tier-card glass ${tier.featured ? 'featured' : ''}">
                ${badgeHtml}
                <div class="tier-name">${tier.name}</div>
                <div class="tier-desc">${tier.desc}</div>
                <div class="tier-price">${display.price}</div>
                <div class="tier-period">${display.period}</div>
                <ul class="tier-features">
                    ${featuresHtml}
                </ul>
                <a href="${tier.buttonLink}" class="${btnClass}">${tier.buttonText}</a>
            </div>
        `;
    }).join('');
}

/**
 * Renders per-song credit bundle cards when in standard licensing mode.
 */
export async function renderCreditPacks() {
    const grid = document.getElementById('pricing-grid');
    if (!grid) return;
    const subtitle = document.getElementById('pricing-subtitle');
    if (subtitle) {
        subtitle.textContent = 'Purchase credit packs to license audio tracks for full transcription and manipulation.';
    }

    try {
        const res = await fetch('/api/pricing');
        if (res.ok) {
            const tiers = await res.json();
            if (tiers.length > 0) {
                grid.innerHTML = tiers.map((tier, index) => `
                    <div class="tier-card glass ${index === 1 ? 'featured' : ''}">
                        ${index === 1 ? '<div class="tier-badge">Best Value</div>' : ''}
                        <div class="tier-name">${tier.name}</div>
                        <div class="tier-desc">${tier.credits} song license credits with lifetime access.</div>
                        <div class="tier-price">$${tier.price.toFixed(2)}</div>
                        <div class="tier-period">One-time purchase</div>
                        <ul class="tier-features">
                            <li><span class="check">✓</span> ${tier.credits} unique audio track licenses</li>
                            <li><span class="check">✓</span> Perpetual access per file hash</li>
                            <li><span class="check">✓</span> Unlimited analysis & playback</li>
                            <li><span class="check">✓</span> No expiration date</li>
                        </ul>
                        <a href="/app#signup" class="btn btn-primary tier-btn">Purchase Pack</a>
                    </div>
                `).join('');
                return;
            }
        }
    } catch (err) {
        console.error('Failed to load credit packs:', err);
    }

    // Fallback credit packs
    grid.innerHTML = `
        <div class="tier-card glass">
            <div class="tier-name">Starter Pack</div>
            <div class="tier-desc">5 song license credits.</div>
            <div class="tier-price">$5.00</div>
            <div class="tier-period">One-time purchase</div>
            <ul class="tier-features">
                <li><span class="check">✓</span> 5 track licenses</li>
                <li><span class="check">✓</span> Perpetual access</li>
            </ul>
            <a href="/app#signup" class="btn btn-primary tier-btn">Purchase Pack</a>
        </div>
    `;
}

// Auto-initialize when DOM is loaded
if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initPricing);
    } else {
        initPricing();
    }
}
